/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import type {
  AccountMeta,
  AccountSignerMeta,
  Address,
  FixedSizeCodec,
  FixedSizeDecoder,
  FixedSizeEncoder,
  Instruction,
  InstructionWithAccounts,
  InstructionWithData,
  ReadonlyAccount,
  ReadonlySignerAccount,
  ReadonlyUint8Array,
  TransactionSigner,
  WritableAccount,
  WritableSignerAccount,
} from "@solana/kit";
import type { ResolvedInstructionAccount } from "@solana/program-client-core";
import type { LockType, LockTypeArgs } from "../types/index.js";
import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
} from "@solana/kit";
import { getAccountMetaFactory } from "@solana/program-client-core";
import { WHIRLPOOL_PROGRAM_ADDRESS } from "../programs/index.js";
import { getLockTypeDecoder, getLockTypeEncoder } from "../types/index.js";

export const LOCK_POSITION_DISCRIMINATOR: ReadonlyUint8Array = new Uint8Array([
  227, 62, 2, 252, 247, 10, 171, 185,
]);

export function getLockPositionDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    LOCK_POSITION_DISCRIMINATOR,
  );
}

export type LockPositionInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountFunder extends string | AccountMeta = string,
  TAccountPositionAuthority extends string | AccountMeta = string,
  TAccountPosition extends string | AccountMeta = string,
  TAccountPositionMint extends string | AccountMeta = string,
  TAccountPositionTokenAccount extends string | AccountMeta = string,
  TAccountLockConfig extends string | AccountMeta = string,
  TAccountWhirlpool extends string | AccountMeta = string,
  TAccountToken2022Program extends
    | string
    | AccountMeta = "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb",
  TAccountSystemProgram extends
    | string
    | AccountMeta = "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountFunder extends string
        ? WritableSignerAccount<TAccountFunder> &
            AccountSignerMeta<TAccountFunder>
        : TAccountFunder,
      TAccountPositionAuthority extends string
        ? ReadonlySignerAccount<TAccountPositionAuthority> &
            AccountSignerMeta<TAccountPositionAuthority>
        : TAccountPositionAuthority,
      TAccountPosition extends string
        ? ReadonlyAccount<TAccountPosition>
        : TAccountPosition,
      TAccountPositionMint extends string
        ? ReadonlyAccount<TAccountPositionMint>
        : TAccountPositionMint,
      TAccountPositionTokenAccount extends string
        ? WritableAccount<TAccountPositionTokenAccount>
        : TAccountPositionTokenAccount,
      TAccountLockConfig extends string
        ? WritableAccount<TAccountLockConfig>
        : TAccountLockConfig,
      TAccountWhirlpool extends string
        ? ReadonlyAccount<TAccountWhirlpool>
        : TAccountWhirlpool,
      TAccountToken2022Program extends string
        ? ReadonlyAccount<TAccountToken2022Program>
        : TAccountToken2022Program,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export interface LockPositionInstructionData {
  discriminator: ReadonlyUint8Array;
  lockType: LockType;
}

export interface LockPositionInstructionDataArgs {
  lockType: LockTypeArgs;
}

export function getLockPositionInstructionDataEncoder(): FixedSizeEncoder<LockPositionInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["lockType", getLockTypeEncoder()],
    ]),
    (value) => ({ ...value, discriminator: LOCK_POSITION_DISCRIMINATOR }),
  );
}

export function getLockPositionInstructionDataDecoder(): FixedSizeDecoder<LockPositionInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["lockType", getLockTypeDecoder()],
  ]);
}

export function getLockPositionInstructionDataCodec(): FixedSizeCodec<
  LockPositionInstructionDataArgs,
  LockPositionInstructionData
> {
  return combineCodec(
    getLockPositionInstructionDataEncoder(),
    getLockPositionInstructionDataDecoder(),
  );
}

export interface LockPositionInput<
  TAccountFunder extends string = string,
  TAccountPositionAuthority extends string = string,
  TAccountPosition extends string = string,
  TAccountPositionMint extends string = string,
  TAccountPositionTokenAccount extends string = string,
  TAccountLockConfig extends string = string,
  TAccountWhirlpool extends string = string,
  TAccountToken2022Program extends string = string,
  TAccountSystemProgram extends string = string,
> {
  funder: TransactionSigner<TAccountFunder>;
  positionAuthority: TransactionSigner<TAccountPositionAuthority>;
  position: Address<TAccountPosition>;
  positionMint: Address<TAccountPositionMint>;
  positionTokenAccount: Address<TAccountPositionTokenAccount>;
  lockConfig: Address<TAccountLockConfig>;
  whirlpool: Address<TAccountWhirlpool>;
  token2022Program?: Address<TAccountToken2022Program>;
  systemProgram?: Address<TAccountSystemProgram>;
  lockType: LockPositionInstructionDataArgs["lockType"];
}

export function getLockPositionInstruction<
  TAccountFunder extends string,
  TAccountPositionAuthority extends string,
  TAccountPosition extends string,
  TAccountPositionMint extends string,
  TAccountPositionTokenAccount extends string,
  TAccountLockConfig extends string,
  TAccountWhirlpool extends string,
  TAccountToken2022Program extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof WHIRLPOOL_PROGRAM_ADDRESS,
>(
  input: LockPositionInput<
    TAccountFunder,
    TAccountPositionAuthority,
    TAccountPosition,
    TAccountPositionMint,
    TAccountPositionTokenAccount,
    TAccountLockConfig,
    TAccountWhirlpool,
    TAccountToken2022Program,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): LockPositionInstruction<
  TProgramAddress,
  TAccountFunder,
  TAccountPositionAuthority,
  TAccountPosition,
  TAccountPositionMint,
  TAccountPositionTokenAccount,
  TAccountLockConfig,
  TAccountWhirlpool,
  TAccountToken2022Program,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? WHIRLPOOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    funder: { value: input.funder ?? null, isWritable: true },
    positionAuthority: {
      value: input.positionAuthority ?? null,
      isWritable: false,
    },
    position: { value: input.position ?? null, isWritable: false },
    positionMint: { value: input.positionMint ?? null, isWritable: false },
    positionTokenAccount: {
      value: input.positionTokenAccount ?? null,
      isWritable: true,
    },
    lockConfig: { value: input.lockConfig ?? null, isWritable: true },
    whirlpool: { value: input.whirlpool ?? null, isWritable: false },
    token2022Program: {
      value: input.token2022Program ?? null,
      isWritable: false,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.token2022Program.value) {
    accounts.token2022Program.value =
      "TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb" as Address<"TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb">;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("funder", accounts.funder),
      getAccountMeta("positionAuthority", accounts.positionAuthority),
      getAccountMeta("position", accounts.position),
      getAccountMeta("positionMint", accounts.positionMint),
      getAccountMeta("positionTokenAccount", accounts.positionTokenAccount),
      getAccountMeta("lockConfig", accounts.lockConfig),
      getAccountMeta("whirlpool", accounts.whirlpool),
      getAccountMeta("token2022Program", accounts.token2022Program),
      getAccountMeta("systemProgram", accounts.systemProgram),
    ],
    data: getLockPositionInstructionDataEncoder().encode(
      args as LockPositionInstructionDataArgs,
    ),
    programAddress,
  } as LockPositionInstruction<
    TProgramAddress,
    TAccountFunder,
    TAccountPositionAuthority,
    TAccountPosition,
    TAccountPositionMint,
    TAccountPositionTokenAccount,
    TAccountLockConfig,
    TAccountWhirlpool,
    TAccountToken2022Program,
    TAccountSystemProgram
  >);
}

export interface ParsedLockPositionInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> {
  programAddress: Address<TProgram>;
  accounts: {
    funder: TAccountMetas[0];
    positionAuthority: TAccountMetas[1];
    position: TAccountMetas[2];
    positionMint: TAccountMetas[3];
    positionTokenAccount: TAccountMetas[4];
    lockConfig: TAccountMetas[5];
    whirlpool: TAccountMetas[6];
    token2022Program: TAccountMetas[7];
    systemProgram: TAccountMetas[8];
  };
  data: LockPositionInstructionData;
}

export function parseLockPositionInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedLockPositionInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 9) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 9,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      funder: getNextAccount(),
      positionAuthority: getNextAccount(),
      position: getNextAccount(),
      positionMint: getNextAccount(),
      positionTokenAccount: getNextAccount(),
      lockConfig: getNextAccount(),
      whirlpool: getNextAccount(),
      token2022Program: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getLockPositionInstructionDataDecoder().decode(instruction.data),
  };
}
